import type { ReportDocument, ReportWarning } from "../../services/report";
import { describeLocation, type ValidationLabels } from "../../utils/validationMessages";

// Shared words and value rules of the report blocks (EOS-13, Master Prompt A31, §20). A report
// consumes frozen evidence: every helper here prints a stored value as received - a decimal
// string stays the exact string, a time is only relabelled as UTC, a code it has no word for is
// printed as the code. Nothing is computed, converted or rounded here.

export const EMPTY_VALUE = "—";

/** A stored time printed as a UTC calendar time, "2026-10-14 11:59:00 UTC"; unreadable text as is. */
export function formatUtc(value: string): string {
  const time = new Date(value);
  return Number.isNaN(time.getTime())
    ? value
    : `${time.toISOString().replace("T", " ").slice(0, 19)} UTC`;
}

// The approval words of the traceability panel (RunTraceabilityPanel), the same on the sheet.
export const APPROVAL_LABELS: Record<string, string> = {
  NOT_SUBMITTED: "Not submitted for review",
  PENDING: "Pending review",
  APPROVED: "Approved",
  REJECTED: "Rejected",
};

export function approvalLabel(status: string): string {
  return APPROVAL_LABELS[status] ?? status;
}

// The A16 words of the design-check states the modules store, module-neutral: the module's own
// summary says "No standard rating fits" or "Coordination not met" for its NO_SOLUTION, the
// document status block says the shared word and prints the stored code beside it.
export const DESIGN_CHECK_LABELS: Record<string, string> = {
  DESIGN_CHECK_PASSED: "Design check passed",
  DESIGN_CHECK_FAILED: "Design check failed",
  NO_STANDARD_SIZE_AVAILABLE: "No standard size available",
  REVIEW_REQUIRED: "Engineering review required",
  VALID: "Calculated",
  WARNING: "Calculated with warnings",
  NO_SOLUTION: "Not met - no solution",
  CALCULATED: "Study calculated",
  INDETERMINATE: "Result indeterminate",
};

/** "Design check passed (DESIGN_CHECK_PASSED)", or the stored code alone when it has no word. */
export function designCheckLabel(status: string): string {
  const label = DESIGN_CHECK_LABELS[status];
  return label === undefined ? status : `${label} (${status})`;
}

// The unit a field carries in its name, the convention of every request and result contract
// (design_current_a, route_length_m, phase_area_mm2 ...). Longest suffix first, so
// "_ohm_per_km" wins over "_km" and "_k_m_per_w" over "_w".
const UNIT_SUFFIXES: ReadonlyArray<readonly [string, string]> = [
  ["_k_m_per_w", "K·m/W"],
  ["_ohm_per_km", "Ω/km"],
  ["_percent", "%"],
  ["_kvar", "kvar"],
  ["_kwh", "kWh"],
  ["_kva", "kVA"],
  ["_mm2", "mm²"],
  ["_ohm", "Ω"],
  ["_kw", "kW"],
  ["_kv", "kV"],
  ["_ka", "kA"],
  ["_km", "km"],
  ["_mm", "mm"],
  ["_ms", "ms"],
  ["_hz", "Hz"],
  ["_va", "VA"],
  ["_a", "A"],
  ["_v", "V"],
  ["_w", "W"],
  ["_m", "m"],
  ["_s", "s"],
  ["_c", "°C"],
  ["_k", "K"],
];

/** The unit of a field by its name's suffix, or null for a dimensionless or textual field. */
export function unitOfField(key: string): string | null {
  const match = UNIT_SUFFIXES.find(([suffix]) => key.endsWith(suffix));
  return match ? match[1] : null;
}

export type SnapshotScalar = string | number | boolean | null;

const PLAIN_DECIMAL = /^[+-]?\d+(?:\.\d+)?$/;

/**
 * One stored scalar as text: a decimal string exactly as stored with its unit, a number as
 * is, a boolean as Yes / No, null as the em dash. Text (a code, a name) never gets a unit.
 */
export function scalarText(value: SnapshotScalar, unit: string | null): string {
  if (value === null) {
    return EMPTY_VALUE;
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (typeof value === "number") {
    return unit ? `${String(value)} ${unit}` : String(value);
  }
  return unit && PLAIN_DECIMAL.test(value) ? `${value} ${unit}` : value;
}

export type SnapshotRow = {
  path: ReadonlyArray<string | number>;
  value: SnapshotScalar | SnapshotScalar[];
};

function isScalar(value: unknown): value is SnapshotScalar {
  return (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Every leaf of a stored snapshot as one row, in the stored order: a nested object is walked,
 * a list of scalars is one row (the size schedule), a list of objects is walked with its index
 * (the loads of a schedule). Nothing is dropped; a value this page cannot read is printed as
 * its JSON text.
 */
export function flattenSnapshot(
  value: unknown,
  path: ReadonlyArray<string | number> = [],
): SnapshotRow[] {
  if (isScalar(value)) {
    return [{ path, value }];
  }
  if (Array.isArray(value)) {
    if (value.every(isScalar)) {
      return [{ path, value: value as SnapshotScalar[] }];
    }
    return value.flatMap((item, index) => flattenSnapshot(item, [...path, index]));
  }
  if (isRecord(value)) {
    return Object.entries(value).flatMap(([key, item]) => flattenSnapshot(item, [...path, key]));
  }
  return [{ path, value: JSON.stringify(value) }];
}

export type InputRowLabel = {
  label: string;
  /** The unit to print beside the value when the label does not carry one. */
  unit: string | null;
};

/**
 * The name a user reads for one stored input: the form's own label (which carries the unit,
 * "Design current (A)") through the module's label map, else the humanized path. When the
 * label carries no unit, the unit of the field's name is printed beside the value instead, so
 * every number prints its unit exactly once.
 */
export function inputRowLabel(
  path: ReadonlyArray<string | number>,
  labels: ValidationLabels,
): InputRowLabel {
  const label = describeLocation(path, labels);
  const last = path[path.length - 1];
  const unit = typeof last === "string" ? unitOfField(last) : null;
  return { label, unit: /\([^)]*\)$/.test(label) ? null : unit };
}

// -- report warnings ------------------------------------------------------------------------------

export type ReportSectionKey =
  | "header"
  | "status"
  | "result"
  | "inputs"
  | "links"
  | "references"
  | "signatures";

// Where each REVIEW_REQUIRED report warning of backend/app/services/report.py is shown: inside
// the section whose value it names, never only in a list at the end. A code this table does
// not know is shown in the document status block, so it is never dropped.
export const REPORT_WARNING_SECTIONS: Record<string, ReportSectionKey> = {
  MODULE_NAME_UNKNOWN: "header",
  PREPARED_BY_MISSING: "signatures",
  EVIDENCE_CHECKSUM_MISMATCH: "signatures",
};

/** The report warnings that belong to one section, in the server's order and words. */
export function reportWarningsOf(document: ReportDocument, section: ReportSectionKey): ReportWarning[] {
  return document.report_warnings.filter(
    (warning) => (REPORT_WARNING_SECTIONS[warning.code] ?? "status") === section,
  );
}

// -- unresolved items -----------------------------------------------------------------------------

const UNRESOLVED = /REVIEW_REQUIRED|NOT_EVALUATED|NOT_ESTABLISHED/;

/**
 * Whether a stored engineering warning is an unresolved item (§20): a check the engine could
 * not make (NOT_EVALUATED), a value the engineer still owes (NOT_ESTABLISHED, which makes the
 * run REVIEW_REQUIRED) or a warning the engine itself marked REVIEW_REQUIRED, read from the
 * warning's code, status or severity as stored.
 */
export function isUnresolved(warning: Record<string, unknown>): boolean {
  return ["code", "status", "severity"].some((key) => {
    const value = warning[key];
    return typeof value === "string" && UNRESOLVED.test(value);
  });
}

/** The text of a stored warning: its code and sentence as received. */
export function warningText(warning: Record<string, unknown>): { code: string; message: string } {
  const code = warning.code;
  const message = warning.message;
  return {
    code: typeof code === "string" ? code : JSON.stringify(code ?? null),
    message: typeof message === "string" ? message : JSON.stringify(message ?? null),
  };
}
